import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Brain,
  User,
  LogOut,
  Activity,
  Shield,
  Users,
  Settings,
  Clock,
  ChevronRight,
  ChevronDown,
  Coins,
  BookUser,
  Gift,
  HelpCircle,
  Sparkles,
  FolderOpen,
  Video,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import { useUserRole } from '@/hooks/useUserRole';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { useReceivedGifts } from '@/hooks/useReceivedGifts';
import { getCreditWarningLevel } from '@/utils/creditWarnings';
import { RoleBadge } from '@/components/RoleBadge';
import { NotificationBell } from '@/components/NotificationBell';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export function AppSidebar() {
  const { state } = useSidebar();
  const { user, signOut } = useAuth();
  const { accessibleCategories } = useAccessibleCategories();
  const { hasPractitionerAccess, hasAdminAccess, role } = useUserRole();
  const { groupSessions } = useGroupSessions();
  const { totalAvailableCredits } = usePractitionerCredits();
  const { unreadCount: unreadGiftsCount } = useReceivedGifts();
  const collapsed = state === 'collapsed';

  const creditWarningLevel = getCreditWarningLevel(totalAvailableCredits);

  // State for collapsible sections
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [libraryExpanded, setLibraryExpanded] = useState(false);

  const activeGroups = groupSessions.filter(group => group.is_active);

  const getCategoryIconStyle = (categoryId: string): string => {
    const base = 'h-4 w-4 category-icon';
    const styleMap: Record<string, string> = {
      'meditation': `${base} category-icon--meditation`,
      'anxiety': `${base} category-icon--anxiety`,
      'massage': `${base} category-icon--massage`,
      'memory': `${base} category-icon--memory`,
      'motivation': `${base} category-icon--motivation`,
      'sleep': `${base} category-icon--sleep`,
      'be_still': `${base} category-icon--be_still`,
      'self_love': `${base} category-icon--self_love`,
      'yoga-meditation': `${base} category-icon--yoga`,
      'zen-af': `${base} category-icon--zen-af`,
      'news-meditation': `${base} category-icon--news-meditation`,
      'sexual-wellness-women': `${base} category-icon--massage`,
      'couples-connection': `${base} category-icon--couples-connection`,
      'relationship-decisions': `${base} category-icon--relationship-decisions`,
      'addiction-recovery-men': `${base} category-icon--addiction-recovery`,
      'home': `${base} category-icon--home`,
    };
    return styleMap[categoryId] || `${base} category-icon--default`;
  };

  const getNavCls = (isActive: boolean) => {
    if (isActive) {
      return "mx-2 mb-1 rounded-lg bg-primary/10 text-primary font-medium";
    }
    return "mx-2 mb-1 rounded-lg hover:bg-accent/50 text-muted-foreground hover:text-foreground";
  };

  const navLinkClasses = "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors";

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      window.location.href = '/auth';
    }
  };

  return (
    <Sidebar
      className={`app-sidebar ${collapsed ? "w-16" : "w-64"} border-r transition-all duration-300`}
      collapsible="icon"
      data-tour="sidebar"
    >
      {/* Header */}
      <SidebarHeader className="p-4 border-b">
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/lovable-uploads/voicethera-logo-new.png"
                alt="VoiceThera"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h2 className="font-semibold text-sm">VoiceThera</h2>
                {role && role !== 'user' && (
                  <RoleBadge role={role} variant="compact" />
                )}
              </div>
            </div>
            <NotificationBell />
          </div>
        ) : (
          <div className="flex justify-center">
            <img
              src="/lovable-uploads/voicethera-logo-new.png"
              alt="VoiceThera"
              className="w-10 h-10 object-contain"
            />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="py-2">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Home */}
              <SidebarMenuItem>
                <NavLink
                  to="/app"
                  end
                  className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                >
                  <Home className="h-4 w-4" />
                  {!collapsed && <span>Home</span>}
                </NavLink>
              </SidebarMenuItem>

              {/* Community */}
              <SidebarMenuItem>
                <NavLink
                  to="/community"
                  className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                >
                  <Sparkles className="h-4 w-4" />
                  {!collapsed && <span>Community</span>}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Categories - Collapsible */}
        <SidebarGroup data-tour="categories">
          <Collapsible open={categoriesExpanded} onOpenChange={setCategoriesExpanded}>
            <CollapsibleTrigger asChild>
              <button
                className={cn(
                  "flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors",
                  collapsed && "justify-center"
                )}
              >
                {!collapsed && <span>Categories</span>}
                {!collapsed && (
                  categoriesExpanded ?
                    <ChevronDown className="h-4 w-4" /> :
                    <ChevronRight className="h-4 w-4" />
                )}
                {collapsed && <Brain className="h-4 w-4" />}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {accessibleCategories.map((category) => (
                    <SidebarMenuItem key={category.id}>
                      <NavLink
                        to={`/category/${category.id}`}
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        {category.icon ? (
                          <category.icon className={getCategoryIconStyle(category.id)} />
                        ) : (
                          <Brain className="h-4 w-4" />
                        )}
                        {!collapsed && <span>{category.name}</span>}
                      </NavLink>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Library - Only for authenticated users */}
        {user && (
          <SidebarGroup data-tour="library">
            <Collapsible open={libraryExpanded} onOpenChange={setLibraryExpanded}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors",
                    collapsed && "justify-center"
                  )}
                >
                  {!collapsed && <span>My Library</span>}
                  {!collapsed && (
                    libraryExpanded ?
                      <ChevronDown className="h-4 w-4" /> :
                      <ChevronRight className="h-4 w-4" />
                  )}
                  {collapsed && <FolderOpen className="h-4 w-4" />}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {/* Sessions */}
                    <SidebarMenuItem>
                      <NavLink
                        to="/sessions/history"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                        data-tour="sessions-link"
                      >
                        <Clock className="h-4 w-4" />
                        {!collapsed && <span>My Sessions</span>}
                      </NavLink>
                    </SidebarMenuItem>

                    {/* Groups */}
                    <SidebarMenuItem>
                      <NavLink
                        to="/groups"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        <Users className="h-4 w-4" />
                        {!collapsed && (
                          <div className="flex items-center gap-2">
                            <span>Groups</span>
                            {activeGroups.length > 0 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                {activeGroups.length}
                              </Badge>
                            )}
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuItem>

                    {/* Biographies */}
                    <SidebarMenuItem>
                      <NavLink
                        to="/biographies"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        <BookUser className="h-4 w-4" />
                        {!collapsed && <span>Biographies</span>}
                      </NavLink>
                    </SidebarMenuItem>

                    {/* Audio Gifts */}
                    <SidebarMenuItem>
                      <NavLink
                        to="/gifts"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        <div className="relative">
                          <Gift className="h-4 w-4" />
                          {unreadGiftsCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-destructive" />
                          )}
                        </div>
                        {!collapsed && (
                          <div className="flex items-center gap-2">
                            <span>Audio Gifts</span>
                            {unreadGiftsCount > 0 && (
                              <Badge variant="destructive" className="h-5 min-w-5 rounded-full p-0 flex items-center justify-center text-xs">
                                {unreadGiftsCount > 9 ? '9+' : unreadGiftsCount}
                              </Badge>
                            )}
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        )}

        {/* Divider */}
        <div className="my-2 mx-4 border-t" />

        {/* Quick Access */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Help */}
              <SidebarMenuItem>
                <NavLink
                  to="/help"
                  className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                  data-tour="help-link"
                >
                  <HelpCircle className="h-4 w-4" />
                  {!collapsed && <span>Help</span>}
                </NavLink>
              </SidebarMenuItem>

              {/* Demo Videos */}
              <SidebarMenuItem>
                <NavLink
                  to="/demo-videos"
                  className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                >
                  <Video className="h-4 w-4" />
                  {!collapsed && <span>Demo Videos</span>}
                </NavLink>
              </SidebarMenuItem>

              {user && (
                <>
                  {/* Profile */}
                  <SidebarMenuItem>
                    <NavLink
                      to="/profile"
                      className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                    >
                      <User className="h-4 w-4" />
                      {!collapsed && <span>Profile</span>}
                    </NavLink>
                  </SidebarMenuItem>

                  {/* Settings */}
                  <SidebarMenuItem>
                    <NavLink
                      to="/preferences"
                      className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      data-tour="settings-link"
                    >
                      <Settings className="h-4 w-4" />
                      {!collapsed && <span>Settings</span>}
                    </NavLink>
                  </SidebarMenuItem>

                  {/* Credits - Only show if not healthy */}
                  {creditWarningLevel !== 'healthy' && (
                    <SidebarMenuItem>
                      <NavLink
                        to="/preferences?tab=credits"
                        className={({ isActive }) => cn(
                          navLinkClasses,
                          getNavCls(isActive),
                          creditWarningLevel === 'critical' && 'animate-pulse'
                        )}
                      >
                        <Coins className={cn(
                          "h-4 w-4",
                          creditWarningLevel === 'critical' ? 'text-destructive' : 'text-orange-500'
                        )} />
                        {!collapsed && (
                          <div className="flex items-center gap-2">
                            <span>Credits</span>
                            <Badge
                              variant={creditWarningLevel === 'critical' ? "destructive" : "outline"}
                              className="text-xs"
                            >
                              {totalAvailableCredits}
                            </Badge>
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  )}

                  {/* Admin Dashboard */}
                  {hasAdminAccess && (
                    <SidebarMenuItem>
                      <NavLink
                        to="/admin"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        <Shield className="h-4 w-4 text-destructive" />
                        {!collapsed && (
                          <div className="flex items-center gap-2">
                            <span>Admin</span>
                            <Badge variant="destructive" className="text-xs px-1.5 py-0">
                              ADMIN
                            </Badge>
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  )}

                  {/* Practitioner Dashboard */}
                  {hasPractitionerAccess && (
                    <SidebarMenuItem>
                      <NavLink
                        to="/practitioner"
                        className={({ isActive }) => cn(navLinkClasses, getNavCls(isActive))}
                      >
                        <Activity className="h-4 w-4 text-primary" />
                        {!collapsed && (
                          <div className="flex items-center gap-2">
                            <span>Practitioner</span>
                            <Badge className="text-xs px-1.5 py-0">
                              PRO
                            </Badge>
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  )}
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-3 border-t">
        {user ? (
          <div className={cn("flex items-center gap-3", collapsed && "flex-col")}>
            <NavLink to="/profile" className="flex-shrink-0" data-tour="profile-link">
              <Avatar className="w-9 h-9 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all">
                <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </NavLink>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            )}
            <Button
              onClick={handleSignOut}
              variant="ghost"
              size="icon"
              className="flex-shrink-0 h-8 w-8"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => window.location.href = '/auth'}
            className="w-full"
            size="sm"
          >
            <User className="h-4 w-4 mr-2" />
            {!collapsed && "Sign In"}
          </Button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
